import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { ServiceArea } from "../../../admin/models/ServiceArea.model";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../redux";
import { getUserCategoriesByType } from "../../../../redux/Actions/UserCategoryActions";
import { UserCategoryTypeEnum } from "../../../../redux/Slices/userCategorySlice";
import { RoleEnum } from "../../../../modules";

interface InternationalServiceAreasProps {
    userId?: string;
    role?: number;
    selectedServiceAreas?: ServiceArea[];
    onEdit?: () => void;
    showEditIcon?: boolean;
}

const InternationalServiceAreas: React.FC<InternationalServiceAreasProps> = ({
    userId,
    role,
    selectedServiceAreas = [],
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useSelector((state: any) => state.auth);
    const targetUserId = userId || user?.id;

    const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>(selectedServiceAreas || []);
    const effectiveRoleId = useMemo(() => role, [role]);

    // Determine initial visible count based on role
    const initialVisibleCount = useMemo(() => {
        if (effectiveRoleId === RoleEnum.internationl_professional) return 2;
        if (effectiveRoleId === RoleEnum.professional) return 1;
        return 1;
    }, [effectiveRoleId]);

    const [visibleCount, setVisibleCount] = useState<number>(initialVisibleCount);

    useEffect(() => {
        const fetchServiceAreas = async () => {
            if (!targetUserId) return;
            try {
                const response: any = await dispatch(
                    getUserCategoriesByType({ userId: String(targetUserId), type: UserCategoryTypeEnum.Profile })
                ).unwrap();
                const areas = response?.serviceAreaIds || [];
                setServiceAreas(Array.isArray(areas) ? areas : []);
            } catch (error) {
                setServiceAreas([]);
            }
        };

        if (!selectedServiceAreas || selectedServiceAreas.length === 0) {
            fetchServiceAreas();
        }
    }, [dispatch, targetUserId, selectedServiceAreas]);

    // Reset visible count when role changes
    useEffect(() => {
        setVisibleCount(initialVisibleCount);
    }, [initialVisibleCount]);

    // Sync with parent updates (e.g., after EditProfileModal saves categories/service areas)
    useEffect(() => {
        if (Array.isArray(selectedServiceAreas)) {
            setServiceAreas(selectedServiceAreas as any);
        }
    }, [selectedServiceAreas]);

    if (!serviceAreas || serviceAreas.length === 0) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-gray-600">{t("internationalServiceAreas.noWorkplaceConfigured")}</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold">{t("internationalServiceAreas.workplaces")}</h3>
            </div>
            <div className="grid grid-cols-1 gap-3">
                {serviceAreas.slice(0, visibleCount).map((area: any) => (
                    <div key={String(area.id)} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-gray-900">{area.name || t("internationalServiceAreas.area")}</h4>
                            {area.emergencyServices && (
                                <Badge variant={undefined} className="bg-red-100 text-red-800 text-xs">{t("internationalServiceAreas.emergency")}</Badge>
                            )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                            {area?.province && area?.country?.name
                                ? `${area.province}, ${area.country.name}`
                                : area?.country?.name || t("internationalServiceAreas.locationNotSpecified")}
                        </p>
                        {Array.isArray(area.serviceTypes) && area.serviceTypes.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {area.serviceTypes.slice(0, 4).map((t: string, idx: number) => (
                                    <Badge key={`${area.id}-${idx}`} variant={undefined} className="bg-gray-100 text-gray-800 text-xs">
                                        {t}
                                    </Badge>
                                ))}
                                {area.serviceTypes.length > 4 && (
                                    <Badge variant={undefined} className="bg-gray-100 text-gray-800 text-xs">
                                        {t("internationalServiceAreas.moreCount", { count: area.serviceTypes.length - 4 })}
                                    </Badge>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {serviceAreas.length > initialVisibleCount && role === RoleEnum.internationl_professional && (
                <div className="flex items-center gap-3">
                    {visibleCount < serviceAreas.length && (
                        <button
                            onClick={() => setVisibleCount(serviceAreas.length)}
                            className="text-blue-600 hover:text-blue-700 text-sm underline"
                        >
                            {t("internationalServiceAreas.showMore")}
                        </button>
                    )}
                    {visibleCount > initialVisibleCount && (
                        <button
                            onClick={() => setVisibleCount(initialVisibleCount)}
                            className="text-blue-600 hover:text-blue-700 text-sm underline"
                        >
                            {t("internationalServiceAreas.showLess")}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default InternationalServiceAreas;


